// Per-user adform draft storage. Draft keys are namespaced as
// `adform:${userId}:${key}` so one account's draft never leaks to another.
// `user_id` is set at login (SmsCode.js) and re-asserted in useAdFormState's
// user-data effect, so it is synchronously available at every read site.

const PREFIX = 'adform:';

export function getUserId() {
    return localStorage.getItem('user_id') || 'anon';
}

export function draftKey(key) {
    return `${PREFIX}${getUserId()}:${key}`;
}

export function readDraft(key) {
    return localStorage.getItem(draftKey(key));
}

export function writeDraft(key, val) {
    localStorage.setItem(draftKey(key), val);
}

export function removeDraft(key) {
    localStorage.removeItem(draftKey(key));
}

// Remove every draft key belonging to the current user (used on publish).
export function removeAllDrafts() {
    const prefix = `${PREFIX}${getUserId()}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) localStorage.removeItem(k);
    }
}

// Logout clear: wipe everything EXCEPT per-user drafts (preserved for the
// user's next session). Replaces the blanket localStorage.clear().
export function clearDraftsForLogout() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && !k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
}

// Demand form drafts — separate keys so a demand never touches the ad
// form's draft. Restored on mount; cleared on successful publish
// (removeDemandDrafts in DemandTab / DemandModal).
export const DEMAND_DRAFTS = {
    title: 'demandTitle',
    description: 'demandDescription',
    maxPrice: 'demandMaxPrice',
    category: 'demandCategory',
    subcategory: 'demandSubcategory',
    quantity: 'demandQuantity',
    measurementUnit: 'demandMeasurementUnit',
};

export function removeDemandDrafts() {
    Object.values(DEMAND_DRAFTS).forEach(key => localStorage.removeItem(draftKey(key)));
}

// Edit-mode drafts — per announcement, so unsaved changes on /edit survive a
// page reload. Cleared when the announcement is saved or the edit page is
// left (see useAdFormState), so a draft never outlives its edit session.
const EDIT_PREFIX = 'adform-edit:';

export function readEditDraft(announcementId, key) {
    if (!announcementId) return null;
    return localStorage.getItem(`${EDIT_PREFIX}${getUserId()}:${announcementId}:${key}`);
}

export function writeEditDraft(announcementId, key, val) {
    if (!announcementId) return;
    localStorage.setItem(`${EDIT_PREFIX}${getUserId()}:${announcementId}:${key}`, val);
}

export function removeEditDraft(announcementId, key) {
    if (!announcementId) return;
    localStorage.removeItem(`${EDIT_PREFIX}${getUserId()}:${announcementId}:${key}`);
}

export function removeAllEditDrafts(announcementId) {
    if (!announcementId) return;
    const prefix = `${EDIT_PREFIX}${getUserId()}:${announcementId}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) localStorage.removeItem(k);
    }
}

// Last location the seller picked in the ad form — kept outside the draft
// namespaces so publish/leave cleanup never wipes it, and /adform always
// pre-fills «Орналасқан жері» with it until the seller picks another one.
const LAST_LOCATION_PREFIX = 'adform-last:';

export function readLastLocation() {
    const raw = localStorage.getItem(`${LAST_LOCATION_PREFIX}${getUserId()}`);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

export function writeLastLocation(location) {
    if (!location || typeof location !== 'object') return;
    localStorage.setItem(`${LAST_LOCATION_PREFIX}${getUserId()}`, JSON.stringify(location));
}

// Last delivery zones the seller published with — like the last location,
// survives draft cleanup so the next new ad pre-selects the same zones.
const LAST_ZONES_PREFIX = 'adform-last-zones:';

export function readLastDeliveryZones() {
    const raw = localStorage.getItem(`${LAST_ZONES_PREFIX}${getUserId()}`);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
    } catch {
        return [];
    }
}

export function writeLastDeliveryZones(deliveryAvailable, ids) {
    if (!deliveryAvailable || !Array.isArray(ids) || ids.length === 0) return;
    localStorage.setItem(`${LAST_ZONES_PREFIX}${getUserId()}`, JSON.stringify(ids));
}

// Saved locations — the seller's recently used spots, offered as one-click
// picks next to «Орналасқан жері». One active location at a time; this list
// just remembers the ones they switch between (most recent first, capped).
const SAVED_LOCATIONS_PREFIX = 'adform-locs:';
const MAX_SAVED_LOCATIONS = 5;

const locationKey = (loc) => [loc.country_id, loc.region_id, loc.district_id].join('-');

export function readSavedLocations() {
    const raw = localStorage.getItem(`${SAVED_LOCATIONS_PREFIX}${getUserId()}`);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function addSavedLocation(location) {
    if (!location || typeof location !== 'object') return readSavedLocations();
    const list = readSavedLocations().filter(saved => locationKey(saved) !== locationKey(location));
    list.unshift(location);
    const trimmed = list.slice(0, MAX_SAVED_LOCATIONS);
    localStorage.setItem(`${SAVED_LOCATIONS_PREFIX}${getUserId()}`, JSON.stringify(trimmed));
    return trimmed;
}

export function removeSavedLocation(index) {
    const list = readSavedLocations();
    if (index < 0 || index >= list.length) return list;
    list.splice(index, 1);
    localStorage.setItem(`${SAVED_LOCATIONS_PREFIX}${getUserId()}`, JSON.stringify(list));
    return list;
}